import type { TargetedEvent } from 'preact';
import { useContext, useState } from 'preact/hooks';

import type { InputTemplateFunction, TemplateFunction, TemplateResult } from 'Components/types';
import { removeMaxCountByField, resetMaxCountByField } from 'Core/actions/request';
import { useCollapsible } from 'Core/hooks/index';
import { createMaxCountSelector } from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import { FacetValue, type FacetValueFull } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import { DisplayMode } from 'Modules/serverApi/types';
import { distribute } from 'Utils/array';
import { cloneSafe } from 'Utils/components';
import { simpleHandler } from 'Utils/roleHandler';

import type { FacetCommonProps } from '../baseFacet';
import { FacetsPanelConfig, FilterInput, SimpleValue } from '../common/index';
import type { SimpleValueTemplateParams } from '../common/simpleValue';

const onInitHiddenValuesSelector = '.cmRepeater_Values';

export default function SimpleFacet({
  facet,
  facet: {
    alwaysShowCount,
    values: { length: valuesCount },
    facetedValues,
    field,
    displayMode,
  },
  config: { customValuesProcessor = (v: FacetValueFull[]) => v },
  templateFunc,
  facetRef,
  commonParams,
  commonParams: { isCollapsed: isFacetCollapsed },
  commonRoles,
}: FacetCommonProps): TemplateResult {
  const dispatch = useDispatch();

  const [filterValue, setFilterValue] = useState('');

  const { showAllAlways, valuesCountToShowFilterInput } = useContext(FacetsPanelConfig);
  const maxCount = useSelector(createMaxCountSelector(field));

  const { isCollapsed: onlyInitValuesDisplayed, toggle: showMoreToggle } = useCollapsible(
    facetRef,
    onInitHiddenValuesSelector,
    !showAllAlways,
    {
      disableCollapse: showAllAlways,
    },
  );

  if (!facetedValues.length) {
    return null;
  }

  const showFilterInput = facetedValues.length >= valuesCountToShowFilterInput;
  const filteredFacetValues = facet.getFilteredValues(filterValue);

  const filterInput = (template: InputTemplateFunction) => {
    if (!showFilterInput) {
      return null;
    }

    const props = {
      template,
      filterValue,
      facetName: facet.name,
      facetField: facet.field,
      onChange: (e: TargetedEvent<HTMLInputElement, Event>) => setFilterValue(e.currentTarget.value),
    };
    return <FilterInput {...props} key={facet.name} />;
  };

  const orderedValues =
    showAllAlways ||
    filteredFacetValues.length <= alwaysShowCount ||
    !onlyInitValuesDisplayed ||
    displayMode === DisplayMode.Tree
      ? filteredFacetValues
      : distribute(filteredFacetValues, (v) => v.isSelected).flat(); // TODO: don't use distribute

  // TODO: get rid of customValuesProcessor
  const valuesToShow = customValuesProcessor(orderedValues, {
    facet,
    showMoreCollapsed: onlyInitValuesDisplayed,
    FacetValue,
  }).map((value) => (templ: TemplateFunction<SimpleValueTemplateParams>) => {
    const props = {
      value,
      template: templ,
    };
    return <SimpleValue {...props} key={value.term} />;
  });

  if (!valuesToShow) {
    return null;
  }

  const sliceIndex = showAllAlways ? orderedValues.length + 1 : alwaysShowCount;
  const [visibleOnInitValues, hiddenOnInitValues] = [
    valuesToShow.slice(0, sliceIndex),
    valuesToShow.slice(sliceIndex),
  ];

  const needShowMore = !showAllAlways && !!hiddenOnInitValues.length;
  const initMaxCount = requestConfig.maxCountByField[field] || null;
  const incrementalShowMore = needShowMore && initMaxCount && filteredFacetValues.length >= initMaxCount;
  const moreValuesToShow = incrementalShowMore
    ? !!maxCount && valuesCount === maxCount
    : onlyInitValuesDisplayed;

  const showMoreHandler =
    incrementalShowMore && !onlyInitValuesDisplayed
      ? () => {
          if (moreValuesToShow) {
            dispatch(removeMaxCountByField(field));
          } else {
            dispatch(resetMaxCountByField(field));
            showMoreToggle();
          }
        }
      : () => showMoreToggle();

  const onClick = simpleHandler({ ...commonRoles, toggleShowMore: showMoreHandler });

  const component = templateFunc.call({
    ...commonParams,
    filterInput,
    showFilterInput,
    inputNotEmpty: filterValue.length,
    needShowMore,
    moreValuesToShow,
    ShowAlwaysValues: visibleOnInitValues, // TODO: check 'isCollapsed'
    Values: !isFacetCollapsed && !onlyInitValuesDisplayed && hiddenOnInitValues,
    valuesCount,
    clearInput: () => setFilterValue(''),
  });
  return cloneSafe(component, facetRef, { onClick });
}
